import { useState, useMemo, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useInfiniteProducts, useRemoveProductFromOrganization, useDeleteProduct } from '@/domains/products/services/products';
import { Product } from '@/shared/types/productTypes';
import { Download, Loader2, Package, Plus } from 'lucide-react';
import ContainerLayout from '@/shared/components/ContainerLayout';
import ProductCard from '@/domains/products/components/ProductCard';
import EmptyView from '@/shared/components/EmptyView';
import AddProductToAccountsDialog from '@/domains/products/dialogs/AddProductToAccountsDialog';
import CreateProductDialog from '@/domains/products/dialogs/CreateProductDialog';
import GenericAlertDialog from '@/shared/components/GenericAlertDialog';
import SearchBar from '@/shared/components/SearchBar';
import { Button } from '@/shared/ui/button';
import { ERROR, SUCCESS } from '@/config/toast-message-config';
import { showErrorWithApiError, showSuccess } from '@/shared/utils/toast-utils';
import UpdateProductDialog from '@/domains/products/dialogs/UpdateProductDialog';
import { useUser } from '@/contexts/UserContext';
import { exportToCsv } from '@/shared/utils/csv-export-utils';
import { Organization } from '@/shared/types/userTypes';
import { ServiceLine } from '@/shared/types/serviceLineTypes';
import { useRouteNavigation } from '@/shared/hooks/use-route-navigation';
import { Separator } from '@/shared/ui/separator';
import { usePermissions } from '@/shared/hooks/use-permissions';
import { Feature, Permission } from '@/shared/types/permissionsTypes';
import { OrgType } from '@/shared/types/userRoleTypes';

export const ProductsPage = () => {
  const { selectedRegionCode } = useUser();
  const { switchOrganizationAndNavigateToServiceLineDetails } = useRouteNavigation();

  const { hasPermission } = usePermissions();
  const canManage = hasPermission(OrgType.PLATFORM, Feature.SUBSCRIPTIONS, Permission.MANAGE);

  const queryClient = useQueryClient();
  const {
    data: productsInfiniteData,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    error,
    refetch: refetchProducts,
  } = useInfiniteProducts(selectedRegionCode);
  const removeProductFromOrganization = useRemoveProductFromOrganization();
  const deleteProduct = useDeleteProduct();

  // State for generic alert dialog
  const [isRemoveProductFromOrganizationDialogOpen, setIsRemoveProductFromOrganizationDialogOpen] = useState(false);
  const [isDeleteProductDialogOpen, setIsDeleteProductDialogOpen] = useState(false);
  const [isAddProductToOrganizationsDialogOpen, setIsAddProductToOrganizationsDialogOpen] = useState(false);
  const [isCreateProductDialogOpen, setIsCreateProductDialogOpen] = useState(false);
  const [isUpdateProductDialogOpen, setIsUpdateProductDialogOpen] = useState(false);
  const pendingRemove = useRef<{ productId: string; organizationId: string } | null>(null);
  const pendingDelete = useRef<{ productId: string } | null>(null);

  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const products = useMemo(() => {
    const pages = productsInfiniteData?.pages ?? [];
    const seen = new Set<string>();
    const merged: Product[] = [];
    for (const page of pages) {
      for (const p of page.results ?? []) {
        const id = p.orbiusProductReferenceId;
        if (id && !seen.has(id)) {
          seen.add(id);
          merged.push(p);
        }
      }
    }
    return merged;
  }, [productsInfiniteData?.pages]);

  const totalCount = productsInfiniteData?.pages[0]?.totalCount ?? 0;

  const filteredProducts = useMemo(() => {
    if (!searchTerm) return products;
    const searchLower = searchTerm.toLowerCase();
    return products.filter(product =>
      product.orbiusProductName?.toLowerCase().includes(searchLower) ||
      product.dataProduct?.dataAmount?.toString().toLowerCase().includes(searchLower) ||
      product.starlinkProductName?.toLowerCase().includes(searchLower) ||
      product.organizations?.some(organization => organization.name?.toLowerCase().includes(searchLower)) ||
      product.organizations?.some(organization => organization.organizationId?.toLowerCase().includes(searchLower))
    );
  }, [products, searchTerm]);

  const refetchProductsData = useCallback(() => {
    if (selectedRegionCode) {
      void queryClient.resetQueries({ queryKey: ['region-products-infinite', selectedRegionCode] });
    }
  }, [queryClient, selectedRegionCode]);

  // Create subscription
  const handleCreateProduct = () => {
    setIsCreateProductDialogOpen(true);
  };

  // Edit subscription
  const handleManageProduct = (product: Product) => {
    setSelectedProduct(product);
    setIsUpdateProductDialogOpen(true);
  };

  // Add subscription to organizations
  const handleAddProductToOrganizations = (product: Product) => {
    setSelectedProduct(product);
    setIsAddProductToOrganizationsDialogOpen(true);
  };

  // Remove product from organization
  const handleRemoveProductFromOrganization = (productId: string, organizationId: string) => {
    pendingRemove.current = { productId, organizationId };
    setIsRemoveProductFromOrganizationDialogOpen(true);
  };

  // Called when user confirms in alert dialog
  const handleConfirmRemoveProductFromOrganization = async () => {
    if (!pendingRemove) return;
    try {
      await removeProductFromOrganization.mutateAsync({
        productId: pendingRemove.current.productId,
        organizationId: pendingRemove.current.organizationId,
      });
      showSuccess(SUCCESS.SUBSCRIPTION.REMOVED_FROM_ACCOUNT);
      refetchProductsData();
    } catch (error) {
      showErrorWithApiError(error, ERROR.SUBSCRIPTION.REMOVE_FROM_ACCOUNT);
    } finally {
      setIsRemoveProductFromOrganizationDialogOpen(false);
      pendingRemove.current = null;
    }
  };

  // Delete product
  const handleDeleteProduct = (productId: string) => {
    pendingDelete.current = { productId };
    setIsDeleteProductDialogOpen(true);
  };

  // Called when user confirms in alert dialog
  const handleConfirmDeleteProduct = async () => {
    if (!pendingDelete) return;
    try {
      await deleteProduct.mutateAsync({ productId: pendingDelete.current.productId });
      showSuccess(SUCCESS.SUBSCRIPTION.DELETED);
      refetchProductsData();
    } catch (error) {
      showErrorWithApiError(error, ERROR.SUBSCRIPTION.DELETE);
    } finally {
      setIsDeleteProductDialogOpen(false);
      pendingDelete.current = null;
    }
  };

  const handleManageServiceLineForOrganization = async (organization: Organization, serviceLine: ServiceLine) => {
    switchOrganizationAndNavigateToServiceLineDetails(organization, serviceLine.serviceLineNumber);
  };

  // Handle export action
  const handleExportAction = () => {
    // If there is no data to export, show an alert
    if (!products || products.length === 0) {
      alert('No data to export');
      return;
    }

    // If the number of service lines is less than the total count, ask for confirmation
    if (products.length < totalCount) {
      if (confirm(`Only ${products.length} of ${totalCount} records will be exported. There are more records available on the next page(s). Please confirm to export?`)) {
        handleExport();
      }
      return;
    }

    // Export the data
    handleExport();
  }

  // Export the data
  const handleExport = () => {
    const csvData = products.map(product => {
      return {
        'Datapool': selectedRegionCode,
        'Product ID': product.orbiusProductReferenceId || '-',
        'Product Name': product.orbiusProductName || '-',
        'Data Amount (GB)': product.dataProduct?.dataAmount || '-',
        'Max Number of User Terminals': product.maxNumberOfUserTerminals || '-',
        'l2vpn Supported': product.l2vpnSupported ? 'Yes' : 'No',
        'Starlink Product Name': product.starlinkProductName || '-',
        'Starlink Product ID': product.productReferenceId || '-',
        'Organization Name': product.organizations?.map(organization => organization.name).join(', ') || '-',
      };
    });

    exportToCsv(csvData, `products_${selectedRegionCode || 'region'}`);
  };

  return (
    <ContainerLayout shouldShowSidebar={true} shouldAllowRegionChange={true} shouldAllowAccountChange={false}>
      <div className="flex flex-col flex-1 p-4 space-y-4">
        {/* Fixed Header Section */}
        <div className="p-4 flex items-center justify-between rounded-lg bg-muted">
          <div>
            <h1 className="text-3xl font-bold">Subscriptions</h1>
            <p className="text-lg">Manage subscriptions for <span className="font-bold text-blue-500">{selectedRegionCode}</span> datapool</p>
          </div>
          <div className="flex flex-row items-center gap-2">
            {handleCreateProduct && canManage && (
              <Button
                variant="default"
                size="default"
                className="gap-2"
                onClick={handleCreateProduct}
                disabled={isLoading}
              >
                <Plus className="h-4 w-4" />
                Create Subscription
              </Button>
            )}
            {products && products.length > 0 && (
              <Button onClick={handleExportAction} className="gap-2">
                <Download className="h-4 w-4" />
                Export
              </Button>
            )}
          </div>
        </div>
        {/* Search Bar */}
        {products && products.length > 0 && (
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search subscriptions by name, data amount, starlink product name, service line name/number or account name..."
          />
        )}

        <Separator />

        {isLoading ? (
          <div className="flex items-center justify-center gap-2">
            <Loader2 className="h-8 w-8 animate-spin" />
            <p className="text-sm">Loading Products...</p>
          </div>
        ) : (
          error ? (
            <EmptyView
              icon={Package}
              title="Error Loading Products"
              description="Error loading products at the moment. Please try again."
              actionLabel="Retry"
              onAction={refetchProducts}
            />
          ) : (
            <>
              {/* Scrollable Content Section */}
              {filteredProducts && filteredProducts.length > 0 ? (
                <div className="flex-1 overflow-y-auto space-y-4">
                  {filteredProducts.map((product) => (
                    <ProductCard
                      key={product.orbiusProductReferenceId}
                      product={product}
                      onAddProductToOrganizations={canManage ? () => handleAddProductToOrganizations(product) : undefined}
                      onManageProduct={canManage ? () => handleManageProduct(product) : undefined}
                      onDeleteProduct={canManage ? () => handleDeleteProduct(product.orbiusProductReferenceId || '') : undefined}
                      onRemoveProductFromOrganization={canManage ? (productId, organizationId) => handleRemoveProductFromOrganization(productId, organizationId) : undefined}
                      onManageServiceLine={(serviceLine) => handleManageServiceLineForOrganization(serviceLine.organization, serviceLine)}
                    />
                  ))}

                  <div className="glass-panel flex flex-col gap-3 border-t p-4 sm:flex-row sm:items-center sm:justify-between rounded-lg">
                    <div className="flex min-w-0 flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-2">
                      <span className="text-md">
                        Loaded <span className="font-medium">{products.length}</span>
                        {' '}of <span className="font-bold">{totalCount}</span> products
                      </span>
                      {filteredProducts.length !== products.length && (
                        <span className="text-sm">
                          ({filteredProducts.length} match current search query)
                        </span>
                      )}
                    </div>
                    {hasNextPage ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="shrink-0 gap-2 self-start sm:self-auto"
                        disabled={isFetchingNextPage}
                        onClick={() => void fetchNextPage()}
                      >
                        {isFetchingNextPage ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Loading…
                          </>
                        ) : (
                          'Load more'
                        )}
                      </Button>
                    ) : null}
                  </div>
                </div>
              ) : (
                <EmptyView
                  icon={Package}
                  title={searchTerm ? "No Matching Products Found" : "No Products Found"}
                  description={searchTerm ? "Try adjusting your search criteria to find what you're looking for." : "There are no products available."}
                  actionLabel={searchTerm && hasNextPage ? "Load More" : undefined}
                  onAction={searchTerm && hasNextPage ? () => void fetchNextPage() : undefined}
                />
              )}
            </>
          ))}
      </div>

      {
        selectedProduct && (
          <AddProductToAccountsDialog
            open={isAddProductToOrganizationsDialogOpen}
            onOpenChange={setIsAddProductToOrganizationsDialogOpen}
            onSuccess={refetchProducts}
            product={selectedProduct}
            selectedRegionCode={selectedRegionCode}
          />
        )
      }

      <CreateProductDialog
        open={isCreateProductDialogOpen}
        onOpenChange={setIsCreateProductDialogOpen}
        onSuccess={refetchProducts}
        selectedRegionCode={selectedRegionCode}
      />

      <UpdateProductDialog
        open={isUpdateProductDialogOpen}
        onOpenChange={setIsUpdateProductDialogOpen}
        onSuccess={refetchProducts}
        product={selectedProduct}
      />

      {/* Generic Alert Dialog for confirming subscription removal */}
      <GenericAlertDialog
        open={isRemoveProductFromOrganizationDialogOpen}
        onOpenChange={setIsRemoveProductFromOrganizationDialogOpen}
        title="Remove Subscription from Organization"
        description="Are you sure you want to remove this subscription from the organization?"
        confirmLabel="Remove"
        cancelLabel="Cancel"
        onConfirm={handleConfirmRemoveProductFromOrganization}
        loading={removeProductFromOrganization.isPending}
      />

      {/* Generic Alert Dialog for confirming product deletion */}
      <GenericAlertDialog
        open={isDeleteProductDialogOpen}
        onOpenChange={setIsDeleteProductDialogOpen}
        title="Delete Subscription"
        description="Are you sure you want to delete this subscription?"
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={handleConfirmDeleteProduct}
        loading={deleteProduct.isPending}
      />
    </ContainerLayout >
  );
};

export default ProductsPage;
